import { useState } from "react";
import { Box, Collapse, Tooltip, styled } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

const NavItem = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "isActive" && prop !== "isChild" && prop !== "isDisabled",
})(({ theme, isActive, isChild, isDisabled }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(1.5),
  padding: theme.spacing(1, 1.5),
  paddingLeft: isChild ? theme.spacing(4.5) : theme.spacing(1.5),
  borderRadius: 6,
  cursor: isDisabled ? "not-allowed" : "pointer",
  fontSize: "0.875rem",
  fontWeight: isActive ? 600 : 400,
  color: isDisabled
    ? theme.palette.text.disabled
    : isActive
      ? theme.palette.primary.main
      : theme.palette.text.primary,
  backgroundColor: isActive ? `${theme.palette.primary.main}15` : "transparent",
  opacity: isDisabled ? 0.6 : 1,
  transition: "all 150ms ease",
  "&:hover": {
    backgroundColor: isDisabled ? "transparent" : `${theme.palette.primary.main}08`,
    color: isDisabled ? theme.palette.text.disabled : theme.palette.primary.main,
  },
}));

const NavItemLeft = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  gap: 10,
}));

const SidebarNavItem = ({ item }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [isExpanded, setIsExpanded] = useState(false);

  const hasChildren = Boolean(item.children?.length);

  const isPathActive = (path) => pathname === path || pathname.startsWith(path);

  const isParentActive = () =>
    hasChildren && item.children.some((child) => isPathActive(child.path));

  const itemActive = !hasChildren && isPathActive(item.path);
  const parentActive = isParentActive();

  const handleClick = () => {
    if (item.disabled) return;
    if (hasChildren) {
      setIsExpanded((prev) => !prev);
    } else {
      navigate(item.path);
    }
  };

  const renderRow = (node, { isChild = false, isActive = false } = {}) => {
    const row = (
      <NavItem
        isChild={isChild}
        isActive={isActive}
        isDisabled={node.disabled}
        onClick={
          node.disabled ? undefined : isChild ? () => navigate(node.path) : handleClick
        }
      >
        <NavItemLeft>
          {node.icon}
          <span>{node.name}</span>
        </NavItemLeft>
        {node.disabled ? (
          <LockOutlinedIcon sx={{ fontSize: 16, opacity: 0.7 }} />
        ) : (
          !isChild &&
          hasChildren &&
          (isExpanded ? (
            <ExpandLessIcon fontSize="small" />
          ) : (
            <ExpandMoreIcon fontSize="small" />
          ))
        )}
      </NavItem>
    );

    if (node.disabled) {
      return (
        <Tooltip title="Coming soon" placement="right" arrow>
          <span>{row}</span>
        </Tooltip>
      );
    }
    return row;
  };

  return (
    <Box>
      {renderRow(item, { isActive: itemActive || parentActive })}

      {hasChildren && (
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Box
            sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 0.5 }}
          >
            {item.children.map((child) => (
              <Box key={child.name}>
                {renderRow(child, {
                  isChild: true,
                  isActive: isPathActive(child.path),
                })}
              </Box>
            ))}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

export default SidebarNavItem;
